import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { getUserDisplayName, getUserLocalLogin, useCurrentUser } from "@/lib/auth";
import { canViewHref } from "@/lib/acl";
import { listAbsenceRequests } from "@/lib/employeeAbsences";
import {
  decoAllowsVacationBubbles,
  decoAllowsVacationDecor,
} from "@/lib/decoPreference";
import { useDecoPreference } from "@/features/deco/DecoPreferenceProvider";
import { useVacationEffects } from "@/hooks/useVacationEffects";
import {
  dismissVacationWishForToday,
  isDevVacationSimulated,
  isOnUrlaubToday,
  isVacationWishDismissedToday,
  pickVacationWishMessage,
} from "@/lib/vacation";

interface VacationContextValue {
  vacationActive: boolean;
  showVacationDecor: boolean;
  showVacationBubbles: boolean;
  wishMessage: string;
  showWishBanner: boolean;
  dismissWish: () => void;
  loading: boolean;
}

const VacationContext = createContext<VacationContextValue | null>(null);

export function VacationProvider({ children }: { children: ReactNode }) {
  const { user } = useCurrentUser();
  const { preference: decoPreference } = useDecoPreference();
  const [onUrlaub, setOnUrlaub] = useState(false);
  const [loading, setLoading] = useState(false);
  const [wishDismissed, setWishDismissed] = useState(() => isVacationWishDismissedToday());

  const simulated = isDevVacationSimulated(user);
  const vacationActive = simulated || onUrlaub;

  const showVacationDecor = vacationActive && decoAllowsVacationDecor(decoPreference);
  const showVacationBubbles = vacationActive && decoAllowsVacationBubbles(decoPreference);

  const wishMessage = useMemo(
    () => pickVacationWishMessage(getUserDisplayName(user)),
    [user],
  );

  useVacationEffects(vacationActive, {
    showBubbles: showVacationBubbles,
    showSeasonDecor: showVacationDecor,
  });

  const reload = useCallback(async () => {
    if (!user) {
      setOnUrlaub(false);
      return;
    }
    if (simulated) {
      setOnUrlaub(true);
      return;
    }

    const login = getUserLocalLogin(user);
    if (!login) {
      setOnUrlaub(false);
      return;
    }

    setLoading(true);
    try {
      // /employee-times ist an Zeiterfassung/Personal gekoppelt (Prod-Gating).
      // Ohne ACL keinen Request – sonst 403/CORS-Noise auf jeder Seite.
      const groups = user.groups || [];
      const canReadAbsences =
        (await canViewHref("/personal/me.html", groups)) ||
        (await canViewHref("/zeiterfassung.html", groups)) ||
        (await canViewHref("/admin/zeiterfassung.html", groups));
      if (!canReadAbsences) {
        setOnUrlaub(false);
        return;
      }

      const year = new Date().getFullYear();
      const absences = await listAbsenceRequests({ user_login: login, year });
      setOnUrlaub(isOnUrlaubToday(absences));
    } catch {
      setOnUrlaub(false);
    } finally {
      setLoading(false);
    }
  }, [user, simulated]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const dismissWish = useCallback(() => {
    dismissVacationWishForToday();
    setWishDismissed(true);
  }, []);

  const showWishBanner =
    vacationActive && decoAllowsVacationDecor(decoPreference) && !wishDismissed;

  const value = useMemo(
    () => ({
      vacationActive,
      showVacationDecor,
      showVacationBubbles,
      wishMessage,
      showWishBanner,
      dismissWish,
      loading,
    }),
    [
      vacationActive,
      showVacationDecor,
      showVacationBubbles,
      wishMessage,
      showWishBanner,
      dismissWish,
      loading,
    ],
  );

  return <VacationContext.Provider value={value}>{children}</VacationContext.Provider>;
}

export function useVacation(): VacationContextValue {
  const ctx = useContext(VacationContext);
  if (!ctx) {
    return {
      vacationActive: false,
      showVacationDecor: false,
      showVacationBubbles: false,
      wishMessage: "",
      showWishBanner: false,
      dismissWish: () => {},
      loading: false,
    };
  }
  return ctx;
}
